// Cognito redirect target (/auth/callback). Completes the Authorization Code +
// PKCE exchange, then navigates to wherever sign-in was started from (falling
// back to the queue). oidc-client-ts strips the code from the URL via
// handleCallback(). On error we show a message + a way to retry.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleCallback } from '../auth/cognito';
import { API_BASE } from '../auth/identity';
import { authorizedReturnTarget } from '../auth/returnTarget';
import type { MyCustomer } from '../api/types';
import { chooseCapacity } from '../components/capacity';
import { Button, Callout } from '../ui/primitives';
import { Spinner } from '../components/ui';

/** Said once on the landing page when a carried link could not be opened with
 *  this sign-in. Never "no longer works": the link was just sent (PM-7). */
export const RETURN_NOTE = "We couldn't open that page with your access. Here is your workspace.";

export function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // React 18 StrictMode double-invokes effects in dev; the code is single-use, so
  // guard against a second exchange attempt.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      try {
        // Land where they were headed before sign-in, not always the queue —
        // a customer invited to /portal/<id> otherwise arrives at a review
        // queue that is not theirs and holds nothing they can act on.
        const { user, returnTo } = await handleCallback();
        // The target came from OIDC state, but authorization can have changed
        // since the email was sent. Read the live grants with THIS token.
        const headers = { Authorization: `Bearer ${user.access_token}` };
        let authorized: string | null = null;
        let hadUnavailableTarget = false;
        try {
          const [grantsResponse, meResponse, invitationResponse] = await Promise.all([
            fetch(`${API_BASE}/my-customers`, { headers }),
            fetch(`${API_BASE}/me`, { headers }),
            fetch(`${API_BASE}/invitation-target/consume`, { method: 'POST', headers }),
          ]);
          if (grantsResponse.ok && meResponse.ok) {
            const grants: MyCustomer[] = await grantsResponse.json();
            const me: { role?: string } = await meResponse.json();
            const isTeam = ['internal_admin', 'owner', 'admin', 'compliance_officer'].includes(me.role ?? '');
            const invitation: { path?: string | null; workspace_path?: string | null } =
              invitationResponse.ok ? await invitationResponse.json() : {};
            authorized = authorizedReturnTarget(returnTo, grants, isTeam)
              ?? authorizedReturnTarget(invitation.path ?? null, grants, isTeam);
            if (!authorized && invitation.workspace_path) {
              authorized = authorizedReturnTarget(invitation.workspace_path, grants, isTeam);
            }
            hadUnavailableTarget = Boolean((returnTo || invitation.path) && !authorized);
            if (authorized) {
              const parts = /^\/(portal|c|customers)\/([0-9a-f-]{36})/i.exec(authorized);
              if (parts) {
                const rows = grants.filter((row) => row.customer_id.toLowerCase() === parts[2].toLowerCase());
                const capacities = new Set(rows.flatMap((row) => row.capacities ?? [row.my_role]));
                const capacity = isTeam ? 'team' : parts[1] === 'c' ? 'attorney'
                  : capacities.has('customer_admin') ? 'customer_admin'
                  : capacities.has('customer') ? 'customer'
                  : capacities.has('customer_readonly') ? 'customer_readonly' : 'customer_records';
                chooseCapacity(parts[2], capacity);
              }
            }
          }
        } catch {
          // A failed grant read cannot authorize a carried destination.
        }
        if (hadUnavailableTarget) sessionStorage.setItem('verityrx-return-note', RETURN_NOTE);
        navigate(authorized ?? '/', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Sign-in could not be completed.');
      }
    })();
  }, [navigate]);

  return (
    <div className="vx">
      <div className="vx-gate">
        <div className="vx-gate__card vx-rise" role={error ? 'alert' : undefined}>
          <span className="vx-wordmark">VerityRx</span>
          {error ? (
            <>
              <h1 style={{ marginTop: 'var(--vx-6)' }}>Sign-in failed</h1>
              <div style={{ marginTop: 'var(--vx-5)' }}>
                <Callout strong>{error}</Callout>
              </div>
              <Button
                variant="primary"
                style={{ width: '100%', marginTop: 'var(--vx-6)' }}
                onClick={() => navigate('/', { replace: true })}
              >
                Try again
              </Button>
            </>
          ) : (
            <p className="vx-muted" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 'var(--vx-6)' }}>
              <Spinner /> Completing sign-in…
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
